import type { QueryClient } from "@tanstack/react-query";

import { api } from "@/api";
import { queryKeys } from "@/api/queryKeys";
import type { AppSession } from "@/session/SessionProvider";
import {
  homeSelection,
  legacyRacingDestination,
} from "@/features/racing/routes";

const PREFETCH_STALE_TIME = 30_000;

export function scheduleWindowAt(nowMs = Date.now()) {
  const center = Math.floor(nowMs / 300_000) * 300_000;
  return {
    from: new Date(center - 30 * 60_000).toISOString(),
    to: new Date(center + 2.5 * 60 * 60_000).toISOString(),
  };
}

export async function prefetchRouteData(
  queryClient: QueryClient,
  path: string,
  session: AppSession | null,
) {
  const url = new URL(path, "https://app.invalid");
  const legacy = legacyRacingDestination(url.pathname, url.search);
  if (legacy) return prefetchRouteData(queryClient, legacy, session);
  path = url.pathname;
  if (path === "/app/schedule") {
    const window = scheduleWindowAt();
    await prefetch(queryClient, ["schedule", window], () =>
      api.schedule(window.from, window.to),
    );
    return;
  }
  if (isHome(path)) {
    const upcoming = await queryClient.ensureQueryData({
      queryKey: ["races", "upcoming", 20],
      queryFn: () => api.upcoming(20),
      staleTime: PREFETCH_STALE_TIME,
    });
    const selected = homeSelection(url.search);
    const raceIds = [
      ...[selected.watch, selected.market].filter((id): id is string =>
        Boolean(id),
      ),
      ...upcoming.races.slice(0, 4).map((race) => race.raceId),
    ].filter((raceId, index, values) => values.indexOf(raceId) === index);
    await settle(
      raceIds.flatMap((raceId) => [
        prefetch(queryClient, ["race", raceId, "card"], () =>
          api.racecard(raceId),
        ),
        prefetch(queryClient, ["race", raceId, "market"], () =>
          api.markets(raceId),
        ),
        ...(selected.tab === "form" &&
        raceId === (selected.market ?? upcoming.races[0]?.raceId)
          ? [
              prefetch(queryClient, ["form-guide", raceId], () =>
                api.formGuide(raceId),
              ),
            ]
          : []),
      ]),
    );
    await settle([
      prefetch(queryClient, ["horses", "in-form", 5], () => api.inForm(5)),
      prefetch(queryClient, ["prizes", "current"], api.currentPrizePeriod),
      prefetch(queryClient, ["season", "current"], api.currentSeason),
    ]);
    return;
  }

  if (path.startsWith("/app/packs")) {
    await settle([
      prefetch(queryClient, ["packs", "series"], api.packSeries),
      prefetch(queryClient, ["live-opens", 20], () => api.liveOpens(20)),
      ...(session
        ? [
            prefetch(queryClient, ["packs", "history", session.playerId], () =>
              api.packHistory(),
            ),
          ]
        : []),
    ]);
    return;
  }

  if (path.startsWith("/app/player")) {
    if (!session) return;
    const horseId = path.split("/app/player/horse/")[1] || null;
    await settle([
      prefetch(queryClient, ["player", session.playerId], api.player),
      prefetch(queryClient, ["stable", session.playerId], () => api.stable()),
      prefetch(queryClient, ["bets", session.playerId], () => api.myCoupons()),
      prefetch(queryClient, ["awards", session.playerId], api.myAwards),
      prefetch(queryClient, ["augments", "catalog"], api.augmentCatalog),
      ...(horseId
        ? [prefetch(queryClient, ["horses", horseId], () => api.horse(horseId))]
        : []),
    ]);
    return;
  }

  if (path.startsWith("/app/marketplace")) {
    await settle([
      prefetch(queryClient, ["market", "listings"], api.marketListings),
      ...(session
        ? [
            prefetch(
              queryClient,
              ["market", "mine", session.playerId],
              api.myMarket,
            ),
          ]
        : []),
    ]);
    return;
  }

  if (path.startsWith("/app/syndicates")) {
    await settle([
      prefetch(queryClient, ["packs", "series"], api.packSeries),
      ...(session
        ? [
            prefetch(
              queryClient,
              ["syndicates", "mine", session.playerId],
              api.mySyndicates,
            ),
          ]
        : []),
    ]);
    return;
  }

  if (path.startsWith("/app/leaderboards")) {
    const ownership = async () => {
      const season = await queryClient.ensureQueryData({
        queryKey: ["season", "current"],
        queryFn: api.currentSeason,
        staleTime: PREFETCH_STALE_TIME,
      });
      await prefetch(
        queryClient,
        ["leaderboards", "ownership", "weekly", season.season.ordinal],
        () => api.ownershipLeaderboard("weekly", season.season.ordinal),
      );
    };
    const wagering = () =>
      prefetch(queryClient, ["leaderboards", "wagering", "weekly"], () =>
        api.wageringLeaderboard("weekly"),
      );
    await settle([
      ...(path.endsWith("/wagering") ? [] : [ownership()]),
      ...(path.endsWith("/ownership") ? [] : [wagering()]),
    ]);
    return;
  }

  if (path.startsWith("/app/verify")) {
    await prefetch(queryClient, ["races", "results", 10], () =>
      api.results(10),
    );
  }
}

export async function warmPublicAppData(queryClient: QueryClient) {
  await settle([
    prefetchRouteData(queryClient, "/app/", null),
    prefetchRouteData(queryClient, "/app/packs", null),
    prefetchRouteData(queryClient, "/app/marketplace", null),
    prefetchRouteData(queryClient, "/app/leaderboards", null),
    prefetchRouteData(queryClient, "/app/verify", null),
  ]);
}

export async function warmAuthenticatedAppData(
  queryClient: QueryClient,
  session: AppSession,
) {
  await settle([
    prefetch(queryClient, queryKeys.balance(session.playerId), api.balance),
    prefetchRouteData(queryClient, "/app/packs", session),
    prefetchRouteData(queryClient, "/app/player", session),
    prefetchRouteData(queryClient, "/app/marketplace", session),
    prefetchRouteData(queryClient, "/app/syndicates", session),
  ]);
}

function prefetch<T>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
) {
  return queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: PREFETCH_STALE_TIME,
  });
}

async function settle(promises: Array<Promise<unknown>>) {
  await Promise.allSettled(promises);
}

function isHome(path: string) {
  return path === "/app" || path === "/app/";
}
